import 'element-closest';
import arrayFrom from 'array-from';
import history from './HistoryHandler';

const ACTIVE_CLASS = 'is-active';
const ARABIC_URL = 'assets/ar.json';
const LANGUAGE_QUERY_PARAM = 'language';
const DEFAULT_LANGUAGE = 'english';

class Translate {
  constructor () {
    const languageSwitcherEl = document.querySelector('.c-header__lang');
    const iframeEl = document.querySelector('iframe.c-hero__content');

    this.state = {
      selectedLanguage: DEFAULT_LANGUAGE,
      isLoadingJSON: false,
      englishTranslation: this.getTranslationFromMarkup(),
      arabicTranslation: null,
      hasIframeLoaded: false
    };

    this.handleLanguageChange(languageSwitcherEl, iframeEl);

    window.addEventListener('message', event => {
      if (event.data === 'website-header-ready') {
        iframeEl.contentWindow.postMessage(this.state.selectedLanguage, '*');
      }
    });

    languageSwitcherEl.addEventListener('click', event => {
      event.preventDefault();

      const linkEl = event.target.closest('a');

      if (linkEl) {
        const href = linkEl.getAttribute('href');
        const isDefaultLanguage = this.checkUrlContainsLanguage(href);

        if (isDefaultLanguage) {
          history.push('/');
        } else {
          history.push(href);
        }

        this.handleLanguageChange(languageSwitcherEl, iframeEl);
      }
    });
  }

  handleLanguageChange (languageSwitcherEl, iframeEl) {
    const queryParamLanguage = this.getLanguageQueryParam(LANGUAGE_QUERY_PARAM);

    this.setLanguage(queryParamLanguage);
    this.setActiveButton(queryParamLanguage, languageSwitcherEl);
    iframeEl.contentWindow.postMessage(queryParamLanguage, '*');
  }

  setLanguage (language) {
    const {
      isLoadingJSON,
      selectedLanguage,
      arabicTranslation
    } = this.state;
    const isRTL = language === 'arabic';
    const html = document.querySelector('html');

    if (isLoadingJSON || language === selectedLanguage) {
      return;
    }

    if (language === 'arabic' && !arabicTranslation) {
      this.setState({ isLoadingJSON: true });
      this.getJSON(ARABIC_URL, response => {
        this.swapText(response, isRTL);
        this.setState({ isLoadingJSON: false });
      });
    } else {
      this.swapText(this.state[`${language}Translation`], isRTL);
    }

    html.lang = isRTL ? 'ar' : 'en';
    this.setState({ selectedLanguage: language });
  }

  setActiveButton (language, languageSwitcher) {
    const switchers = arrayFrom(languageSwitcher.children);

    switchers.forEach( switcherEl => {
      const isActiveLanguage = this.checkUrlContainsLanguage(switcherEl.href, language);

      if (isActiveLanguage) {
        switcherEl.classList.add(ACTIVE_CLASS);
      } else {
        switcherEl.classList.remove(ACTIVE_CLASS);
      }
    });
  }

  checkUrlContainsLanguage (url, language = DEFAULT_LANGUAGE) {
    const regExp = new RegExp(language);
    return regExp.test(url);
  }

  swapText (newLanguage, isRTL) {
    const RTL_U_CLASS = 'u-rtl';
    const RTL_STATE_CLASS = 'is-rtl';
    const keys = Object.keys(newLanguage);

    keys.forEach( key => {
      const els = document.querySelectorAll(`[data-translate="${key}"]`);

      if (!els.length) {
        return;
      }

      arrayFrom(els).forEach( el => {
        if (isRTL) {
          el.parentElement.classList.add(RTL_U_CLASS);
          el.parentElement.classList.add(RTL_STATE_CLASS);
        } else {
          el.parentElement.classList.remove(RTL_U_CLASS);
          el.parentElement.classList.remove(RTL_STATE_CLASS);
        }

        if (el.getAttribute('data-translate-child-elements') !== null) {
          const translations = newLanguage[key];
          el.innerHTML = '';

          translations.forEach( (translation) => {
            const tagName = this.getTagNameFromTranslation(translation);
            const classes = this.getUnformattedClassesFromTranslation(translation);
            const translationText = this.getTranslationText(translation);
            const newElement = document.createElement(tagName);

            newElement.setAttribute('class', classes);
            newElement.textContent = translationText;

            newElement.innerHTML = this.getTextWithLinks(newElement.textContent);

            el.appendChild(newElement);
          });
        } else {
          const textNode = this.getFirstTextNode(el);

          if (textNode) {
            textNode.textContent = newLanguage[key];
          }
        }
      });
    });
  }

  getTextWithLinks (text) {
    const regex = /(\[(.*?)\]\()(.+?)(\))/g;
    return text.replace(regex, '<a href="$3">$2</a>');
  }

  getTranslationFromMarkup () {
    const els = document.querySelectorAll('[data-translate]');

    return arrayFrom(els).reduce( (translation, el) => {
      const translateChildElements = el.getAttribute('data-translate-child-elements');
      const translate = el.getAttribute('data-translate');

      if (!translate) {
        return translation;
      }

      if (translateChildElements !== null) {
        translation[translate] = arrayFrom(el.children).map( this.convertElToTranslation.bind(this) )
      } else {
        const textNode = this.getFirstTextNode(el);

        if (textNode) {
          translation[translate] = textNode.textContent;
        }
      }

      return translation;
    }, {});
  }

  convertElToTranslation (el) {
    const tagName = el.tagName;
    const classes = this.getFormattedClasses(el);
    const text = this.extractTextFromElement(el);

    if (text) {
      return [text, tagName];
    }

    return ['', tagName + classes];
  }

  extractTextFromElement (el) {
    const nodes = arrayFrom(el.childNodes);

    return nodes.map( node => {
      if ( node.nodeName.toLowerCase() === 'a') {
        return `[${node.textContent}](${node.getAttribute('href')})`;
      }

      return node.textContent;
    }).join('');
  }

  getTranslationText (translation) {
    if (typeof translation === 'string') {
      return translation;
    }

    return translation[0];
  }

  getTagNameFromTranslation (translation) {
    const DEFAULT_TAG = 'p';
    const markupData = translation[1];

    if (typeof translation === 'string' || !markupData) {
      return DEFAULT_TAG;
    }

    return markupData.split('.')[0];
  }

  getUnformattedClassesFromTranslation (translation) {
    const DEFAULT_CLASSES = '';
    const markupData = translation[1];

    if (typeof translation === 'string' || !markupData) {
      return DEFAULT_CLASSES;
    }

    const classes = markupData.split('.').slice(1).join(' ');

    if (!classes) {
      return DEFAULT_CLASSES;
    }

    return classes;
  }

  getFormattedClasses (el) {
    const classes = el.getAttribute('class');

    if (!classes) {
      return '';
    }

    return '.' + classes.replace(/ /g, '.');
  }

  getFirstTextNode (el) {
    const TEXT = 3;

    return arrayFrom(el.childNodes)
      .filter( node => node.nodeType === TEXT)[0]
  }

  getJSON (url, callback) {
    const xhr = new XMLHttpRequest();
    const OK = 200;

    xhr.open('GET', url, true);
    xhr.responseType = 'json';
    xhr.addEventListener('load', () => {
      const status = xhr.status;

      if (status === OK) {
        const { response } = xhr;
        let parsedResponse = response;

        if (typeof response === 'string') {
          parsedResponse = JSON.parse(response);
        }

        callback(parsedResponse);
      } else {
        callback(status);
      }
    });
    xhr.send();
  }

  getLanguageQueryParam (paramName, defaultVal = DEFAULT_LANGUAGE) {
    const val = history.location.search
      .replace(/^\?/, '')
      .split('&')
      .reduce( (value, string) => {
        const KEY = 0;
        const VALUE = 1;
        const pair = string.split('=');

        if (pair[KEY] === paramName) {
          value = pair[VALUE];
        }

        return value;
      }, undefined);

    return val || defaultVal;
  }

  setState (obj) {
    this.state = Object.assign({}, this.state, obj);
  }
}

export default Translate;
